/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @demo-content — DELETE THIS FILE when the catalogue serves `swatch` image groups.
 *
 * WHY IT EXISTS. Live NM and live BG paint each PDP colour choice as a round
 * colour disc. Ours paints a black rectangle holding the colour NAME. The disc
 * mechanism is already complete and tokenised (`theme/tokens/pdp.css` gates it on
 * `:has([data-slot='swatch-dot'])`), and it renders correctly when a marker is
 * injected — the only missing input is an image on the colour variation value.
 *
 * `product.imageGroups` in the bkcb-004 catalogue carries exactly ONE viewType,
 * `hi-res`. Measured 2026-08-21 over 35 PDPs across both brands and 8 queries:
 * 86 image groups, 86 of them `hi-res`, ZERO `swatch`. So
 * `findImageGroupBy(..., { viewType: 'swatch' })` — which both
 * `use-variation-attributes.ts` and `product-utils.ts` call — can never match,
 * and `value.image` is always `undefined`.
 *
 * WHAT THIS DERIVES, AND WHY IT IS NOT A GUESS. The NMG media CDN serves a
 * per-colour swatch object as a SUFFIX SIBLING of the product photo. Every
 * colour-scoped `hi-res` group already carries a genuine catalogue link of the
 * form `.../f_auto,q_auto/01/<brand>_<style>_<colorcode>_<suffix>`; replacing
 * the trailing suffix with `_s` addresses the swatch for that exact colour.
 * This is suffix substitution on a real link — nothing about the style or the
 * colour code is synthesized.
 *
 * MEASURED 2026-08-21, with a negative control in the same run:
 *   `_s`     200 on 171/171 sampled products — 148 bases harvested from search
 *            tiles on 10 queries x 2 brands, plus 23 derived from PDP image
 *            groups. Every response decoded as a 36x36 baseline JPEG.
 *   `_zq9`   (bogus suffix, negative control) 404 on 171/171, with a constant
 *            body length per brand (852 NM / 1017 BG). The control never once
 *            returned 200, so the `_s` result stands.
 *   content  126 of 148 `_s` images have a per-pixel RGB standard deviation
 *            below 8 (median 0 — perfectly uniform); the product photos they
 *            were derived from have a median of 51.8. The remainder are
 *            multi-tone and printed fabric swatches, confirmed by eye. None is
 *            a downscaled product photograph.
 *   identity 23/23 derived swatches match their colour NAME semantically —
 *            BLACK -> #000000/#141315, NAVY BLUE -> #465163, TAN -> #ad6e59,
 *            COGNAC -> #6e3f2b, CREAM -> #f7e8d5, LTBLUE -> #94b5cc.
 *
 * THE FAILURE MODE, AND HOW IT IS BOUNDED. A miss does NOT degrade gracefully:
 * the CDN answers an unknown suffix with HTTP 404 AND a valid 76x95 JPEG of the
 * brand monogram, so a browser paints it rather than firing `onerror`. That is
 * why the guard below is a WHITELIST rather than a fallback — the derivation is
 * applied only to the exact URL family that was measured at 171/171, and any
 * link that does not match is refused, leaving `value.image` undefined and the
 * existing text-chip branch rendering unchanged. Widening this pattern without
 * re-running the probe re-opens that failure.
 *
 * WHAT THE REAL SOURCE IS. An `<image-group view-type="swatch">` per colour in
 * the product catalogue, which SFCC surfaces on `imageGroups` with no code
 * change at all — `findImageGroupBy(..., 'swatch')` then matches and
 * `value.image` is populated by the platform. When that lands, delete this file
 * and its single call site in `product-view/product-info.tsx`, and remove the
 * row from the package `CLAUDE.md` demo-content list.
 *
 * NOT SOLVED BY THE bkcb_dev CATALOGUE EXPORT. That export does carry 11
 * `view-type="swatch"` groups, but 10 of the 11 hold a six-digit HEX COLOUR
 * (`E10BA1` for MAGENTA, `565457` for NOIR) where an image path belongs — a
 * different convention that would need a different render path — and its 155
 * product ids have ZERO overlap with the ids this storefront serves.
 */
import type { ShopperProducts } from '@/scapi';

/**
 * The one product-image URL family this derivation is measured against.
 *
 * Host set is not decorative: across the 2026-08-21 sweep every
 * `imageGroups[].images[].link` came from `media.neimanmarcus.com` (32) or
 * `media.bergdorfgoodman.com` (54) — 86 of 86, no other host. The canonical
 * CloudFront aliases are accepted too because `lib/images/cdn-hostname.ts`
 * rewrites into them and both spellings were probed.
 *
 * Group 1 is everything up to and including the colour code; the trailing
 * `_<suffix>` is what gets replaced.
 */
const NMG_PRODUCT_IMAGE_PATTERN =
    /^(https:\/\/(?:media\.neimanmarcus\.com|media\.bergdorfgoodman\.com|d2zuh0aaxtlvpo\.cloudfront\.net|d14ffjdh2tf6td\.cloudfront\.net)\/f_auto,q_auto\/\d+\/(?:nm|bg)_\d+_\d+)_[a-z0-9]+$/i;

/** The measured swatch suffix. See the module docblock for the 171/171 result. */
const SWATCH_SUFFIX = '_s';

/**
 * The colour-scoped image group for one colour value, or `undefined`.
 *
 * Deliberately does NOT filter on `viewType`: the point of this module is that
 * the catalogue serves only `hi-res`, and pinning a viewType here would make the
 * derivation break for exactly the reason it exists. What identifies the group
 * is that it is scoped to `color = value` and carries at least one image.
 */
const findColorScopedGroup = (
    imageGroups: ShopperProducts.schemas['ImageGroup'][],
    colorValue: string
): ShopperProducts.schemas['ImageGroup'] | undefined =>
    imageGroups.find(
        (group) =>
            Boolean(group.images?.length) &&
            group.variationAttributes?.some(
                (attribute) => attribute.id === 'color' && attribute.values?.some(({ value }) => value === colorValue)
            )
    );

/**
 * Derive the swatch image for one colour variation value.
 *
 * Returns `undefined` — leaving the caller's existing no-image branch to render
 * — unless ALL of these hold:
 *   1. the attribute is `color`,
 *   2. the product has an image group scoped to exactly this colour value,
 *   3. that group's first image `link` matches the measured NMG URL family.
 *
 * Any other shape is refused rather than transformed. See the docblock: an
 * unknown suffix returns a paintable 404 body, so a permissive rule would put a
 * brand monogram inside the disc instead of falling back to the text chip.
 *
 * @param attributeId - Variation attribute id (only `color` is derived)
 * @param value - The colour attribute value, e.g. `NAVY BLUE`
 * @param valueName - Display name used as the image `alt`
 * @param imageGroups - `product.imageGroups` straight off SCAPI
 */
export function deriveColorSwatchImage({
    attributeId,
    value,
    valueName,
    imageGroups,
}: {
    attributeId: string | undefined;
    value: string | undefined;
    valueName?: string;
    imageGroups: ShopperProducts.schemas['ImageGroup'][] | undefined;
}): ShopperProducts.schemas['Image'] | undefined {
    if (attributeId !== 'color' || !value || !imageGroups?.length) return undefined;

    const link = findColorScopedGroup(imageGroups, value)?.images?.[0]?.link;
    if (!link) return undefined;

    const match = NMG_PRODUCT_IMAGE_PATTERN.exec(link);
    if (!match) return undefined;

    return {
        link: `${match[1]}${SWATCH_SUFFIX}`,
        alt: valueName || value,
    };
}
